import React from 'react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowRight, MessageCircle, Users } from 'lucide-react';
import { OtrosTramiteActionsMenu } from './OtrosTramiteActions';
import OtroTramiteIndicators from './OtroTramiteIndicators';
import { ArchivosButton } from './ArchivosButton';
import {
  formatAssignedUsersLabel,
  formatPersonasLabel,
  type OtrosTramiteRowProps,
} from './OtrosTramiteRow';
import { formatDate } from '../utils';
import { useOtrosTramitesColumnVisibility } from '../contexts/OtrosTramitesColumnVisibilityContext';

/**
 * Versión en ficha de OtrosTramiteRow para pantallas angostas.
 * Respeta la misma visibilidad de columnas y expone las mismas acciones.
 */
export const OtrosTramiteCard: React.FC<OtrosTramiteRowProps> = ({
  tramite,
  onView,
  onEdit,
  onDelete,
  onRetirar,
  onViewRetiroHistorial,
  onManageCobros,
  onManageFacturas,
  onManageArchivos,
  onSendWhatsAppRetiro,
  onViewOtorgantes,
  onViewResponsables,
  onViewGarantes,
  onEditAssignedUser,
  isSelected,
  onToggleSelect,
}) => {
  const { isColumnVisible } = useOtrosTramitesColumnVisibility();
  const detalle = (tramite.detalle ?? '').trim();
  const copiasDisponibles = tramite.copiasTotales - tramite.copiasActuales;
  const puedeRetirar = copiasDisponibles > 0;
  const retiroCompleto = !puedeRetirar;
  const assignedUsersLabel = formatAssignedUsersLabel(tramite.assignedUsers ?? []);

  const personas = [
    {
      key: 'otorgantes',
      label: 'Otorgantes',
      value: formatPersonasLabel(tramite.otorgantes),
      count: tramite.otorgantesCount,
      onClick: onViewOtorgantes,
    },
    {
      key: 'responsables',
      label: 'Responsables',
      value: formatPersonasLabel(tramite.responsables),
      count: tramite.responsablesCount,
      onClick: onViewResponsables,
    },
    {
      key: 'garantes',
      label: 'Garantes',
      value: formatPersonasLabel(tramite.garantes ?? []),
      count: tramite.garantesCount ?? 0,
      onClick: onViewGarantes,
    },
  ] as const;

  const hasFooter =
    (isColumnVisible('factura') && onManageFacturas) ||
    (isColumnVisible('archivos') && onManageArchivos) ||
    isColumnVisible('indicadores');

  return (
    <article
      className={`rounded-lg border p-3 text-sm shadow-sm ${isSelected ? 'border-blue-200 bg-blue-50' : 'border-gray-100 bg-white'}`}
    >
      <header className="flex items-start gap-3">
        <Checkbox
          className="mt-0.5"
          checked={isSelected}
          onCheckedChange={() => onToggleSelect(tramite.id)}
          disabled={!puedeRetirar}
          aria-label={`Seleccionar trámite ${tramite.id}`}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
            {isColumnVisible('id') && <span className="font-medium text-gray-900">#{tramite.id}</span>}
            {isColumnVisible('fecha') && <span>{formatDate(tramite.fecha)}</span>}
          </div>
          {isColumnVisible('tipo') && (
            <p className="font-semibold text-gray-900 break-words">{tramite.tipo}</p>
          )}
        </div>
        {isColumnVisible('acciones') && (
          <OtrosTramiteActionsMenu
            tramite={tramite}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            onManageCobros={onManageCobros}
          />
        )}
      </header>

      {isColumnVisible('detalle') && detalle && (
        <p className="mt-2 line-clamp-2 text-gray-600" title={detalle}>{detalle}</p>
      )}

      <dl className="mt-2 space-y-1">
        {personas.filter((persona) => isColumnVisible(persona.key)).map((persona) => (
          <div key={persona.key} className="flex items-center gap-2">
            <dt className="w-28 shrink-0 text-xs text-gray-500">{persona.label}</dt>
            <dd className="min-w-0 flex-1">
              {persona.count > 0 && persona.onClick ? (
                <button
                  type="button"
                  className="inline-flex max-w-full items-center gap-1.5 text-left text-primary hover:underline"
                  onClick={() => persona.onClick?.(tramite)}
                >
                  <Users className="h-4 w-4 shrink-0" />
                  <span className="truncate">{persona.value}</span>
                </button>
              ) : (
                <span className="inline-flex max-w-full items-center gap-1.5">
                  <Users className="h-4 w-4 shrink-0" />
                  <span className="truncate">{persona.value}</span>
                </span>
              )}
            </dd>
          </div>
        ))}
        {isColumnVisible('asignados') && (
          <div className="flex items-center gap-2">
            <dt className="w-28 shrink-0 text-xs text-gray-500">Asignados</dt>
            <dd className="min-w-0 flex-1">
              {onEditAssignedUser ? (
                <button
                  type="button"
                  className="block max-w-full truncate text-left text-primary hover:underline"
                  title={assignedUsersLabel === '-' ? 'Asignar responsable' : assignedUsersLabel}
                  onClick={() => onEditAssignedUser(tramite)}
                >
                  {assignedUsersLabel === '-' ? 'Asignar' : assignedUsersLabel}
                </button>
              ) : (
                <span className="block truncate">{assignedUsersLabel}</span>
              )}
            </dd>
          </div>
        )}
        {isColumnVisible('copias') && (
          <div className="flex items-center gap-2">
            <dt className="w-28 shrink-0 text-xs text-gray-500">Copias</dt>
            <dd className="flex min-w-0 flex-1 items-center gap-2">
              <span className="font-medium">{`${tramite.copiasActuales}/${tramite.copiasTotales}`}</span>
              {puedeRetirar ? (
                <Button
                  variant="link"
                  size="xs"
                  className="p-0 text-primary"
                  onClick={() => onRetirar(tramite)}
                >
                  <ArrowRight className="h-3 w-3" />
                  Retirar
                </Button>
              ) : onViewRetiroHistorial ? (
                <button
                  type="button"
                  className="font-medium text-gray-400 hover:underline"
                  onClick={() => onViewRetiroHistorial(tramite)}
                  title="Ver historial de retiros"
                >
                  Completo
                </button>
              ) : (
                <span className="font-medium text-gray-400">Completo</span>
              )}
              {onSendWhatsAppRetiro && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto h-8 w-8 p-0 text-green-600 hover:text-green-700"
                  onClick={() => onSendWhatsAppRetiro(tramite)}
                  title="Enviar WhatsApp retiro"
                >
                  <MessageCircle className="h-4 w-4" />
                  <span className="sr-only">Enviar WhatsApp retiro</span>
                </Button>
              )}
            </dd>
          </div>
        )}
      </dl>

      {hasFooter && (
        <footer className="mt-2 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-2">
          {isColumnVisible('factura') && onManageFacturas && (
            <ArchivosButton
              tramite={tramite}
              onClick={onManageFacturas}
              label="Factura"
              count={tramite.facturasCount ?? 0}
            />
          )}
          {isColumnVisible('archivos') && onManageArchivos && (
            <ArchivosButton
              tramite={tramite}
              onClick={onManageArchivos}
              label="Archivos"
              count={tramite.archivosCount ?? 0}
            />
          )}
          {isColumnVisible('indicadores') && (
            <div className="ml-auto">
              <OtroTramiteIndicators tramite={tramite} onManageCobros={onManageCobros} />
            </div>
          )}
        </footer>
      )}
    </article>
  );
};

OtrosTramiteCard.displayName = 'OtrosTramiteCard';
